import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppData } from '@/contexts/AppDataContext';
import { decodeText, formatStat } from '@/lib/text';

/** Split title so the last phrase can take the neon accent. */
function splitTitle(title) {
  const text = decodeText(title || '').trim();
  if (!text) {
    return { lead: 'Driving your success,', accent: 'STEP by STEP' };
  }

  const comma = text.lastIndexOf(',');
  if (comma !== -1 && comma < text.length - 1) {
    return {
      lead: text.slice(0, comma + 1).trim(),
      accent: text.slice(comma + 1).trim(),
    };
  }

  const parts = text.split(/\s+/);
  if (parts.length <= 2) {
    return { lead: text, accent: '' };
  }

  const cut = Math.max(2, Math.floor(parts.length * 0.55));
  return {
    lead: parts.slice(0, cut).join(' '),
    accent: parts.slice(cut).join(' '),
  };
}

const Hero = () => {
  const { language } = useLanguage();
  const { appData } = useAppData();
  const t = appData?.content?.hero?.[language] || {};
  const companyInfo = appData?.company || {};
  const isRTL = language === 'ar';

  const { lead, accent } = useMemo(
    () => splitTitle(t.title || 'Driving your success, STEP by STEP'),
    [t.title]
  );

  const brand = decodeText(t.badge || companyInfo?.name || 'ROI Agency');
  const tagline = decodeText(
    t.tagline ||
      'In creativity and technology, we make your ideas a reality.'
  );

  const stats = [
    { value: formatStat(companyInfo.stats?.experience), label: t?.experience || 'Years of experience' },
    { value: formatStat(companyInfo.stats?.projects), label: t?.projects || 'Executed projects' },
    { value: formatStat(companyInfo.stats?.clients), label: t?.clients || 'Number of clients' },
  ];

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-dark z-10 grain-overlay grain-overlay--hero"
    >
      <div className="absolute inset-0 z-0 hero-static-bg" aria-hidden="true" />

      {/* IG-style tech grid */}
      <div className="absolute inset-0 z-[1] ig-grid opacity-40 pointer-events-none" aria-hidden="true" />

      {/* Vignette */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        aria-hidden="true"
        style={{
          background: `
            radial-gradient(ellipse 65% 50% at 50% 42%, rgba(5,6,7,0.2), rgba(5,6,7,0.88) 75%),
            linear-gradient(180deg, rgba(5,6,7,0.65) 0%, transparent 30%, transparent 58%, rgba(5,6,7,0.95) 100%)
          `,
        }}
      />

      {/* Neon halo behind focal content — like IG bloom */}
      <div
        className="neon-halo animate-neon-pulse z-[1] w-[min(70vw,520px)] h-[min(70vw,520px)] left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2"
        aria-hidden="true"
      />

      {/* Layered watermark like IG posts */}
      <div className="ig-watermark z-[1] top-[38%]" aria-hidden="true">
        {language === 'ar' ? 'حلول' : 'SOLUTIONS'}
      </div>

      {/* Main composition — center-weighted like the feed */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="container mx-auto px-5 sm:px-8 lg:px-12 w-full pt-28 pb-10 md:pb-14">
          <div className={`max-w-4xl mx-auto text-center ${isRTL ? 'rtl' : ''}`}>
            <p className="hero-reveal font-display text-xs sm:text-sm font-bold uppercase tracking-[0.45em] text-neon mb-8 md:mb-10">
              {brand}
            </p>

            <h1 className="hero-reveal-delay-1 font-display font-bold text-white leading-[0.95] tracking-tight uppercase text-[clamp(2.4rem,7vw,5.25rem)] mb-6 md:mb-8">
              <span className="block">{lead}</span>
              {accent ? (
                <span className="block mt-2 md:mt-3 text-neon text-[clamp(2.2rem,6.5vw,4.75rem)]">
                  {accent}
                </span>
              ) : null}
            </h1>

            <p className="hero-reveal-delay-2 text-base sm:text-lg md:text-xl text-white/65 leading-relaxed max-w-xl mx-auto mb-9 md:mb-11 text-balance">
              {tagline}
            </p>

            <div
              className={`hero-reveal-delay-3 flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-center ${
                isRTL ? 'sm:flex-row-reverse' : ''
              }`}
            >
              <a
                href="#contact"
                className={`group inline-flex items-center justify-center gap-2 min-h-[48px] px-8 py-3.5 bg-primary text-dark font-bold uppercase tracking-wide text-sm rounded-xl hover:bg-primary-hover transition-colors shadow-glow ${
                  isRTL ? 'flex-row-reverse' : ''
                }`}
              >
                {decodeText(t.consultation) || 'Free consultation'}
                <ArrowRight
                  className={`w-4 h-4 transition-transform ${isRTL ? 'rotate-180' : ''} ${
                    !isRTL ? 'group-hover:translate-x-0.5' : 'group-hover:-translate-x-0.5'
                  }`}
                  aria-hidden="true"
                />
              </a>
              <a
                href="#services"
                className="inline-flex items-center justify-center min-h-[48px] px-7 py-3.5 text-white font-semibold uppercase tracking-wide text-sm rounded-xl border border-primary/35 hover:border-primary hover:text-primary hover:shadow-glow-sm transition-all"
              >
                {decodeText(t.services) || 'Our services'}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 border-t border-primary/15 bg-dark/80">
        <div className="container mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-3 divide-x divide-primary/15">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="py-5 md:py-6 px-2 sm:px-4 text-center"
              >
                <div className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-neon tabular-nums">
                  {stat.value}
                </div>
                <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/40 leading-snug">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
